import { DEV } from 'cc/env';
import { _decorator, v4, director, Director, Vec3, Vec2, v3, toRadian } from 'cc';
import ShaderManipulator from './ShaderManipulator';

const { ccclass, property } = _decorator;

@ccclass('PositionLRTBProvider')
export default class PositionLRTBProvider extends ShaderManipulator {
    @property
    private _useCorrectAlgorithm = false;

    private _lrtbDirty = false;

    @property({ tooltip: DEV && 'Use correct algorithm to calculate LRTB property' })
    public get useCorrectAlgorithm() {
        return this._useCorrectAlgorithm;
    }
    public set useCorrectAlgorithm(value: boolean) {
        if (this._useCorrectAlgorithm === value) {
            return;
        }
        this._useCorrectAlgorithm = value;
        this.updateUseCorrectAlgorithmProperty();
    }

    public resetInEditor() {
        // to ensure new component use correct algorithm by default
        this.useCorrectAlgorithm = true;
    }

    protected registerTargetEvent() {
        super.registerTargetEvent();
        director.on(Director.EVENT_BEFORE_DRAW, this.checkVertDirty, this);
        director.on(Director.EVENT_AFTER_DRAW, this.tryUpdateLRTBProperty, this);
    }

    protected unregisterTargetEvent() {
        super.unregisterTargetEvent();
        director.off(Director.EVENT_BEFORE_DRAW, this.checkVertDirty, this);
        director.off(Director.EVENT_AFTER_DRAW, this.tryUpdateLRTBProperty, this);
    }

    protected syncProperty() {
        this.updateLRTBProperty();
        this.updateUseCorrectAlgorithmProperty();
    }

    protected checkVertDirty() {
        if (this.node.hasChangedFlags || this.target?.renderData?.vertDirty) {
            this.markLRTBDirty();
        }
    }

    protected markLRTBDirty() {
        this._lrtbDirty = true;
    }

    protected tryUpdateLRTBProperty() {
        if (this._lrtbDirty) {
            this.updateLRTBProperty();
            this._lrtbDirty = false;
        }
    }

    protected updateLRTBProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        if (!this.target.renderData?.chunk) {
            return;
        }

        // get world positions of 4 corners from vertex buffer
        const { renderData } = this.target;
        const worldPositions: Vec3[] = [];
        for (let offset = 0; offset < renderData.chunk.vb.length; offset += renderData.floatStride) {
            const [x, y, z] = renderData.chunk.vb.slice(offset, offset + 3);
            worldPositions.push(new Vec3(x, y, z));
        }

        const eulerAngles = this.node.worldRotation.getEulerAngles(v3());
        const angleRad = toRadian(eulerAngles.x !== 0 ? 180 - eulerAngles.z : eulerAngles.z);
        const rotateCenter = this.node.worldPosition.toVec2();

        // Step 1: 把所有點旋轉回去 (-θ)
        const rotatedBack = worldPositions.map((p) => rotatePoint(p.toVec2(), rotateCenter, -angleRad));

        const rotatedBackX = rotatedBack.map((p) => p.x);
        const rotatedBackY = rotatedBack.map((p) => p.y);
        // Step 2: 找 min/max
        const [left, right, top, bottom] = [Math.min(...rotatedBackX), Math.max(...rotatedBackX), Math.max(...rotatedBackY), Math.min(...rotatedBackY)];

        const cornersBack = [
            new Vec2(left, bottom), // 左下
            new Vec2(right, bottom), // 右下
            new Vec2(right, top), // 右上
            new Vec2(left, top), // 左上
            new Vec2((left + right) * 0.5, (top + bottom) * 0.5), // 中心
        ];

        // Step 3: 再旋轉回去 (+θ)
        const [leftBottom, rightBottom, rightTop, leftTop, center] = cornersBack.map((p) => rotatePoint(p, rotateCenter, angleRad));

        // set lrtb property to shader
        pass.setUniform(pass.getHandle('leftBottom'), leftBottom);
        pass.setUniform(pass.getHandle('rightBottom'), rightBottom);
        pass.setUniform(pass.getHandle('rightTop'), rightTop);
        pass.setUniform(pass.getHandle('leftTop'), leftTop);
        pass.setUniform(pass.getHandle('centerPosition'), center);
    }

    protected updateUseCorrectAlgorithmProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        pass.setUniform(pass.getHandle('useCorrectAlgorithm'), v4(this.useCorrectAlgorithm ? 1 : 0, 0));
    }
}

// 旋轉一個點 p，繞著 rotateCenter 旋轉 angleRad 弧度
function rotatePoint(p: Vec2, rotateCenter: Vec2, angleRad: number): Vec2 {
    const cosA = Math.cos(angleRad);
    const sinA = Math.sin(angleRad);
    const dx = p.x - rotateCenter.x;
    const dy = p.y - rotateCenter.y;
    return new Vec2(rotateCenter.x + dx * cosA - dy * sinA, rotateCenter.y + dx * sinA + dy * cosA);
}
